import type { ReactNode } from 'react';
import {
  formatCurrency,
  isPositiveAmount,
  type EventNeeds,
  type ExpenseCashFilter,
} from '@peles/shared';

const money = (n: number) => formatCurrency(n, { wholeDollars: true });

export interface BudgetStripProps {
  totals: EventNeeds['totals'];
  /** Open the Expenses view on these cash filters (empty: every booked line). */
  onShowExpenses: (cash: ExpenseCashFilter[]) => void;
}

function Figure({
  label,
  value,
  tone = 'text-gray-900',
  onClick,
  hint,
}: {
  label: string;
  value: ReactNode;
  tone?: string;
  onClick?: () => void;
  hint?: string;
}) {
  const body = (
    <>
      <dt className="text-xs text-gray-500">{label}</dt>
      <dd className={`font-semibold ${tone}`}>{value}</dd>
    </>
  );
  return onClick ? (
    <button
      type="button"
      onClick={onClick}
      title={hint}
      className="text-left rounded-lg bg-gray-50 px-3 py-2 min-h-11 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
    >
      <dl>{body}</dl>
    </button>
  ) : (
    <dl className="rounded-lg bg-gray-50 px-3 py-2">{body}</dl>
  );
}

/**
 * The event's money at a glance, over the Plan: what the needs are budgeted
 * at, what is committed, what is still being sourced, what is paid, what falls
 * due in the next 30 days and what is overdue. The money figures open the
 * Expenses view on the lines they count. Every figure is the server's
 * (`GET /events/:id/needs` totals) — no second arithmetic here.
 */
export function BudgetStrip({ totals, onShowExpenses }: BudgetStripProps) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 text-sm">
      <Figure label="Budget" value={money(totals.budget)} />
      <Figure
        label="Committed"
        value={money(totals.committed)}
        onClick={() => onShowExpenses([])}
        hint="Show the booked lines"
      />
      <Figure
        label="Still sourcing"
        value={
          <>
            {totals.sourcing} {totals.sourcing === 1 ? 'need' : 'needs'}
            {totals.bestQuotes > 0 && (
              <span className="block font-normal text-gray-600 text-xs">
                best quotes {money(totals.bestQuotes)}
              </span>
            )}
          </>
        }
      />
      <Figure
        label="Paid"
        value={money(totals.paid)}
        // Every booked line: its Total Paid is this figure. "Paid" lines alone
        // would leave out what was paid on lines still owing.
        onClick={() => onShowExpenses([])}
        hint="Show the booked lines and what was paid on them"
      />
      <Figure
        label="Due in 30 days"
        value={money(totals.dueSoon)}
        onClick={() => onShowExpenses(['due30'])}
        hint="Show what falls due in the next 30 days"
      />
      <Figure
        label="Overdue"
        value={money(totals.overdue)}
        tone={isPositiveAmount(totals.overdue) ? 'text-red-600' : 'text-gray-900'}
        onClick={() => onShowExpenses(['overdue'])}
        hint="Show what is overdue"
      />
    </div>
  );
}
